import React, { useState } from "react";
import { X, Loader2, Plus, RotateCcw } from "lucide-react";
import { addBusinessDays, addCalendarDays } from "../../utils/dateUtils";
import { detectCompanyKey, generateNextCompanyPoNumber, isStandardGeneratedPoNumber } from "../../utils/poNumberUtils";

export default function POReceiveModal({
  isOpen,
  onClose,
  onSubmit,
  newPoForm,
  setNewPoForm,
  error,
  loading,
  customers,
  paymentMethods,
  companyLogos,
  existingPos = [],
  onAddCustomerClick,
  isEditMode = false
}) {
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);

  if (!isOpen) return null;

  const handleAutoAssignPoNumber = () => {
    const selectedLogo = (companyLogos || []).find(l => l.url === newPoForm.logoUrl);
    const compKey = detectCompanyKey({
      logoUrl: newPoForm.logoUrl,
      companyName: selectedLogo?.name || newPoForm.companyName,
      clientName: newPoForm.clientName,
      companyLogos
    });
    const nextNum = generateNextCompanyPoNumber({
      companyKey: compKey,
      existingPos,
      companyLogos
    });
    setNewPoForm(prev => ({
      ...prev,
      poNumber: nextNum,
      companyName: selectedLogo?.name || prev.companyName || ""
    }));
  };

  const handleSelectCustomer = (customer) => {
    // If issuing company logo is not set, customer name may indicate the company sequence
    const compKey = detectCompanyKey({
      logoUrl: newPoForm.logoUrl,
      companyName: newPoForm.companyName,
      clientName: customer.name,
      companyLogos
    });

    const isCurrentGenerated = !newPoForm.poNumber || isStandardGeneratedPoNumber(newPoForm.poNumber);
    const updatedPoNumber = (isCurrentGenerated && compKey !== "default")
      ? generateNextCompanyPoNumber({ companyKey: compKey, existingPos, companyLogos })
      : newPoForm.poNumber;

    setNewPoForm({
      ...newPoForm,
      poNumber: updatedPoNumber,
      clientName: customer.name,
      shipToAddress: customer.shipAddress || customer.address || "",
      shipToAddress2: customer.shipAddress2 || customer.address2 || "",
      shipToCity: customer.shipCity || customer.city || "",
      shipToState: customer.shipState || customer.state || "",
      shipToZip: customer.shipZip || customer.zip || "",
      markFor: customer.markFor || "",
      paymentMethod: customer.preferredPaymentMethod || ""
    });
    setShowCustomerSuggestions(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <div className="modal-header">
          <h3>{isEditMode ? "Modify Estimate Details" : "Log Client EST Request"}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={onSubmit}>
          {error && (
            <div className="login-error-alert animate-fade-in" style={{ marginBottom: "1rem" }}>
              {error}
            </div>
          )}

          {/* 1. Company Selection (First choice on the form) */}
          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label" htmlFor="logoUrl" style={{ fontWeight: 600 }}>Company *</label>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <select
                id="logoUrl"
                className="form-input"
                value={newPoForm.logoUrl || ""}
                onChange={(e) => {
                  const selectedUrl = e.target.value;
                  const selectedLogo = (companyLogos || []).find(l => l.url === selectedUrl);
                  const selectedName = selectedLogo?.name || "";

                  const compKey = detectCompanyKey({
                    logoUrl: selectedUrl,
                    companyName: selectedName,
                    clientName: newPoForm.clientName,
                    companyLogos
                  });

                  // If poNumber is blank or matches standard auto-generated formats, refresh to company sequence
                  const shouldAutoUpdate = !newPoForm.poNumber || isStandardGeneratedPoNumber(newPoForm.poNumber);
                  const updatedPoNumber = shouldAutoUpdate
                    ? generateNextCompanyPoNumber({ companyKey: compKey, existingPos, companyLogos })
                    : newPoForm.poNumber;

                  setNewPoForm({
                    ...newPoForm,
                    logoUrl: selectedUrl,
                    companyName: selectedName,
                    poNumber: updatedPoNumber
                  });
                }}
                style={{
                  flex: 1,
                  backgroundColor: "var(--bg-input)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "4px",
                  padding: "0.5rem 0.75rem",
                  cursor: "pointer"
                }}
              >
                <option value="" style={{ backgroundColor: "var(--bg-card)" }}>-- Select Company / No Logo --</option>
                {companyLogos.map((logo, idx) => (
                  <option
                    key={logo.id}
                    value={logo.url}
                    style={{ backgroundColor: "var(--bg-card)", color: "var(--text-primary)" }}
                  >
                    {logo.name || `Logo ${idx + 1}`} {logo.isActive ? "(Default Active)" : ""}
                  </option>
                ))}
              </select>
              {newPoForm.logoUrl && (
                <div style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "4px",
                  border: "1px solid var(--border-color)",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.05)"
                }}>
                  <img src={newPoForm.logoUrl} alt="EST Logo Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              )}
            </div>
          </div>

          <div className="grid-2" style={{ gap: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div className="form-group">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label className="form-label" htmlFor="poNumber" style={{ margin: 0 }}>EST Number *</label>
                <button
                  type="button"
                  onClick={handleAutoAssignPoNumber}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--primary)",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    padding: 0
                  }}
                  title="Auto-generate next sequential EST number for selected company"
                >
                  <RotateCcw size={11} /> Auto-sequence
                </button>
              </div>
              <input
                id="poNumber"
                type="text"
                required
                className="form-input"
                style={{ marginTop: "0.35rem" }}
                placeholder="e.g. 1200 or 26-001 or 2000"
                value={newPoForm.poNumber}
                onChange={(e) => setNewPoForm({ ...newPoForm, poNumber: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="customerPoNumber">Customer PO # (Optional)</label>
              <input
                id="customerPoNumber"
                type="text"
                className="form-input"
                placeholder="e.g. CUST-PO-991"
                value={newPoForm.customerPoNumber || ""}
                onChange={(e) => setNewPoForm({ ...newPoForm, customerPoNumber: e.target.value })}
              />
            </div>
          </div>
          <div className="grid-2" style={{ gap: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div className="form-group">
              <label className="form-label" htmlFor="receivedFromCustomerDate">RFQ Received *</label>
              <input
                id="receivedFromCustomerDate"
                type="date"
                required
                className="form-input"
                value={newPoForm.receivedFromCustomerDate || newPoForm.date || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setNewPoForm({ 
                    ...newPoForm, 
                    receivedFromCustomerDate: val,
                    date: newPoForm.date || val
                  });
                }}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="expirationDate">Estimate Expiration Date</label>
              <input
                id="expirationDate"
                type="date"
                className="form-input"
                value={newPoForm.expirationDate || ""}
                onChange={(e) => setNewPoForm({ ...newPoForm, expirationDate: e.target.value })}
              />
            </div>
          </div>
          <div className="grid-2" style={{ gap: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div className="form-group">
              <label className="form-label" htmlFor="date">Request Date</label>
              <input
                id="date"
                type="date"
                required
                className="form-input"
                value={newPoForm.date}
                onChange={(e) => {
                  const newReqDateStr = e.target.value;
                  const savedDueDateDays = localStorage.getItem("global_po_due_date_days");
                  const dueDays = savedDueDateDays !== null ? parseInt(savedDueDateDays, 10) : 3;
                  const newDueDateStr = addBusinessDays(newReqDateStr, dueDays);
                  setNewPoForm({ 
                    ...newPoForm, 
                    date: newReqDateStr,
                    dueDate: newDueDateStr,
                    receivedFromCustomerDate: newPoForm.receivedFromCustomerDate || newReqDateStr,
                    expirationDate: newPoForm.expirationDate || addCalendarDays(newReqDateStr, 30)
                  });
                }}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="dueDate">Due Date</label>
              <input
                id="dueDate"
                type="date"
                required
                className="form-input"
                value={newPoForm.dueDate}
                onChange={(e) => setNewPoForm({ ...newPoForm, dueDate: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={!!newPoForm.isPriority}
                onChange={(e) => setNewPoForm({ ...newPoForm, isPriority: e.target.checked })}
                style={{ width: "16px", height: "16px", accentColor: "#f59e0b", cursor: "pointer" }}
              />
              <span style={{ fontSize: "0.9rem", fontWeight: 600, color: newPoForm.isPriority ? "#f59e0b" : "var(--text-primary)" }}>
                Priority EST
              </span>
              {newPoForm.isPriority && (
                <span className="priority-pill" style={{ marginLeft: "0.25rem" }}>PRIORITY</span>
              )}
            </label>
          </div>

          <div className="form-group" style={{ position: "relative" }}>
            <label className="form-label" htmlFor="clientName">Client / Company Name *</label>
            <input
              id="clientName"
              type="text"
              required
              className="form-input"
              placeholder="e.g. Apex Manufacturing Ltd."
              value={newPoForm.clientName}
              onChange={(e) => {
                const val = e.target.value;
                const matchingCustomer = (customers || []).find(
                  c => c.name?.trim().toLowerCase() === val.trim().toLowerCase()
                );
                if (matchingCustomer) {
                  setNewPoForm({ 
                    ...newPoForm, 
                    clientName: val,
                    shipToAddress: matchingCustomer.shipAddress || matchingCustomer.address || newPoForm.shipToAddress,
                    shipToAddress2: matchingCustomer.shipAddress2 || matchingCustomer.address2 || newPoForm.shipToAddress2,
                    shipToCity: matchingCustomer.shipCity || matchingCustomer.city || newPoForm.shipToCity,
                    shipToState: matchingCustomer.shipState || matchingCustomer.state || newPoForm.shipToState,
                    shipToZip: matchingCustomer.shipZip || matchingCustomer.zip || newPoForm.shipToZip,
                    markFor: matchingCustomer.markFor || newPoForm.markFor,
                    paymentMethod: matchingCustomer.preferredPaymentMethod || newPoForm.paymentMethod
                  });
                } else {
                  setNewPoForm({ ...newPoForm, clientName: val });
                }
                setShowCustomerSuggestions(true);
              }}
              onFocus={() => setShowCustomerSuggestions(true)}
              onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
              autoComplete="off"
            />
            
            {showCustomerSuggestions && (
              <div className="tag-suggestions-dropdown" style={{ 
                position: "absolute", 
                top: "100%", 
                left: 0, 
                right: 0, 
                backgroundColor: "var(--bg-card)", 
                border: "1px solid var(--border-color)", 
                borderRadius: "4px", 
                zIndex: 1000, 
                maxHeight: "180px", 
                overflowY: "auto", 
                marginTop: "0.25rem",
                boxShadow: "0 4px 12px rgba(0,0,0,0.5)"
              }}>
                {customers
                  .filter(c => !newPoForm.clientName || c.name.toLowerCase().includes(newPoForm.clientName.toLowerCase()))
                  .map(customer => (
                    <div
                      key={customer.id}
                      onMouseDown={() => handleSelectCustomer(customer)}
                      style={{ 
                        padding: "0.5rem 0.75rem", 
                        cursor: "pointer", 
                        color: "var(--text-primary)"
                      }}
                      className="suggestion-item"
                    >
                      <div style={{ fontWeight: 600 }}>{customer.name}</div>
                      {customer.city && (
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                          {customer.city}, {customer.state}
                        </div>
                      )}
                    </div>
                  ))
                }
                {newPoForm.clientName && customers.filter(c => c.name.toLowerCase().includes(newPoForm.clientName.toLowerCase())).length === 0 && (
                  <div style={{ padding: "0.5rem 0.75rem", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                    No matching customers
                  </div>
                )}
                <div
                  onMouseDown={() => {
                    setShowCustomerSuggestions(false);
                    if (onAddCustomerClick) onAddCustomerClick();
                  }}
                  style={{ 
                    padding: "0.6rem 0.75rem", 
                    cursor: "pointer", 
                    color: "var(--primary)",
                    fontWeight: "600",
                    borderTop: "1px solid var(--border-color)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    backgroundColor: "rgba(255, 255, 255, 0.02)"
                  }}
                  className="suggestion-item add-client-item"
                >
                  <Plus size={14} style={{ color: "var(--primary)" }} /> Add Client
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="title">Project / Quote Subject</label>
            <input
              id="title"
              type="text"
              required
              className="form-input"
              placeholder="e.g. Zone 4B Storage Racking"
              value={newPoForm.title}
              onChange={(e) => setNewPoForm({ ...newPoForm, title: e.target.value })}
            />
          </div>
          {(() => {
            const matchingCustomer = (customers || []).find(
              c => c.name?.trim().toLowerCase() === newPoForm.clientName?.trim().toLowerCase()
            );
            const customerPaymentMethod = matchingCustomer?.preferredPaymentMethod;
            return (
              <div className="form-group">
                <label className="form-label" htmlFor="paymentMethod">
                  Payment Method {customerPaymentMethod ? `(Customer Preferred: ${customerPaymentMethod})` : ""}
                </label>
                <select
                  id="paymentMethod"
                  className="form-input"
                  value={newPoForm.paymentMethod || ""}
                  onChange={(e) => setNewPoForm({ ...newPoForm, paymentMethod: e.target.value })}
                >
                  <option value="">-- Select Payment Method --</option>
                  {customerPaymentMethod && (
                    <option value={customerPaymentMethod}>
                      {customerPaymentMethod} (Customer Preferred)
                    </option>
                  )}
                  {paymentMethods && paymentMethods
                    .filter(pm => pm.name !== customerPaymentMethod)
                    .map(pm => (
                      <option key={pm.id} value={pm.name}>{pm.name}</option>
                    ))}
                </select>
              </div>
            );
          })()}

          <div style={{ marginTop: "1.25rem", borderTop: "1px solid var(--border-color)", paddingTop: "1rem" }}>
            <h4 style={{ margin: "0 0 0.75rem 0", fontSize: "0.95rem", color: "var(--primary)" }}>Ship To Address</h4>
            <div className="form-group">
              <label className="form-label" htmlFor="shipToAddress">Address 1 *</label>
              <input
                id="shipToAddress"
                type="text"
                required
                className="form-input"
                placeholder="e.g. 123 Main St"
                value={newPoForm.shipToAddress}
                onChange={(e) => setNewPoForm({ ...newPoForm, shipToAddress: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="shipToAddress2">Address 2</label>
              <input
                id="shipToAddress2"
                type="text"
                className="form-input"
                placeholder="e.g. Suite 100"
                value={newPoForm.shipToAddress2}
                onChange={(e) => setNewPoForm({ ...newPoForm, shipToAddress2: e.target.value })}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 0.7fr 0.8fr", gap: "0.75rem" }}>
              <div className="form-group">
                <label className="form-label" htmlFor="shipToCity">City *</label>
                <input
                  id="shipToCity"
                  type="text"
                  required
                  className="form-input"
                  placeholder="e.g. Austin"
                  value={newPoForm.shipToCity}
                  onChange={(e) => setNewPoForm({ ...newPoForm, shipToCity: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="shipToState">State *</label>
                <input
                  id="shipToState"
                  type="text"
                  required
                  className="form-input"
                  placeholder="e.g. TX"
                  value={newPoForm.shipToState}
                  onChange={(e) => setNewPoForm({ ...newPoForm, shipToState: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="shipToZip">Zip *</label>
                <input
                  id="shipToZip"
                  type="text"
                  required
                  className="form-input"
                  placeholder="e.g. 78701"
                  value={newPoForm.shipToZip}
                  onChange={(e) => setNewPoForm({ ...newPoForm, shipToZip: e.target.value })}
                />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: "0.75rem" }}>
              <label className="form-label" htmlFor="markFor">Mark For (M/F)</label>
              <input
                id="markFor"
                type="text"
                className="form-input"
                placeholder="e.g. Warehouse Dock #2 / Attn: John"
                value={newPoForm.markFor || ""}
                onChange={(e) => setNewPoForm({ ...newPoForm, markFor: e.target.value })}
              />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <Loader2 className="spinner-icon" size={16} /> : null}
              {isEditMode ? "Save Changes" : "Create EST Log"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
